// src/utils/categoryTracking.ts

import { CategoryHierarchy, ItemTrackingConfig, TrackingType } from '../types/category';

/**
 * Get the item tracking configuration for a category.
 * Handles backward compatibility with the old bulkItemBehavior field.
 * 
 * Priority order:
 * 1. New itemTrackingConfig (if present)
 * 2. Old bulkItemBehavior (convert to new format)
 * 3. Default to SERIALIZED (safer for law enforcement)
 * 
 * @param category - The category to get tracking config for
 * @returns ItemTrackingConfig with tracking type and requirements
 */
export function getItemTrackingConfig(
  category: CategoryHierarchy | undefined
): ItemTrackingConfig {
  if (!category) {
    // Default: serialized (safer for law enforcement - everything tracked)
    return {
      trackingType: 'SERIALIZED',
      requiresSerialNumber: true
    };
  }
  
  // Priority 1: Use new itemTrackingConfig if present
  if (category.itemTrackingConfig) {
    return category.itemTrackingConfig;
  }
  
  // Priority 2: Convert old bulkItemBehavior to new format
  if (category.bulkItemBehavior) {
    // Old BULK + isConsumable=true → CONSUMABLE
    // Old BULK + isConsumable=false → BULK_TRACKED
    const trackingType: TrackingType = category.bulkItemBehavior.isConsumable 
      ? 'CONSUMABLE' 
      : 'BULK_TRACKED';
    return {
      trackingType,
      requiresSerialNumber: false
    };
  }
  
  // Priority 3: Default to serialized for undefined config
  // This ensures new subcategories without explicit config default to stricter tracking
  return {
    trackingType: 'SERIALIZED',
    requiresSerialNumber: true
  };
}

/**
 * Validate that a category's tracking configuration is valid.
 * 
 * Rules:
 * - Only subcategories (parentCategoryId != null) can have itemTrackingConfig
 * - Parent categories are organizational only
 * 
 * @param category - The category to validate
 * @returns Validation result with error message if invalid
 */
export function validateTrackingConfig(
  category: CategoryHierarchy
): { valid: boolean; error?: string } {
  // Parent categories (no parentCategoryId) cannot have tracking config
  if (!category.parentCategoryId && category.itemTrackingConfig) {
    return {
      valid: false,
      error: 'Only subcategories can have item tracking configuration. Parent categories are organizational only.'
    };
  }
  
  return { valid: true };
}

/**
 * Get a human-readable label for the tracking type.
 * Used for displaying badges in UI.
 * 
 * @param config - The tracking configuration
 * @returns Display label like "Serialized", "Bulk Tracked", or "Consumable"
 */
export function getTrackingTypeLabel(config: ItemTrackingConfig): string {
  switch (config.trackingType) {
    case 'SERIALIZED':
      return 'Serialized';
    case 'BULK_TRACKED':
      return 'Bulk Tracked';
    case 'CONSUMABLE':
      return 'Consumable';
    default:
      return 'Unknown';
  }
}

/**
 * Get a short description of what the tracking type means.
 * Used for tooltips and help text.
 * 
 * @param config - The tracking configuration
 * @returns Description text
 */
export function getTrackingTypeDescription(config: ItemTrackingConfig): string {
  switch (config.trackingType) {
    case 'SERIALIZED':
      return 'Each unit is tracked individually with a unique serial number. Full chain of custody. Used for firearms, radios, body armor, and other accountable equipment.';
    case 'BULK_TRACKED':
      return 'Items tracked by quantity. Assignment records track who has what. Returns restore inventory. Used for helmets, tactical vests, and reusable equipment.';
    case 'CONSUMABLE':
      return 'Items tracked by stock level. Assignment records created for officer gear view. Returns are disposed, not restocked. Used for uniforms, gloves, and ammunition.';
    default:
      return 'Unknown tracking type.';
  }
}

/**
 * Check if a category requires serial numbers for items.
 * 
 * @param category - The category to check
 * @returns true if serial numbers are required
 */
export function requiresSerialNumber(category: CategoryHierarchy | undefined): boolean {
  const config = getItemTrackingConfig(category);
  return config.requiresSerialNumber;
}

/**
 * Check if a category tracks items by quantity (BULK_TRACKED or CONSUMABLE).
 * 
 * @param category - The category to check
 * @returns true if items are tracked by quantity
 */
export function isQuantityTracked(category: CategoryHierarchy | undefined): boolean {
  const config = getItemTrackingConfig(category);
  return config.trackingType === 'BULK_TRACKED' || config.trackingType === 'CONSUMABLE';
}

/**
 * Check if returns for this category restore inventory.
 * SERIALIZED and BULK_TRACKED restore inventory on return.
 * CONSUMABLE items are disposed on return.
 * 
 * @param category - The category to check
 * @returns true if returns restore inventory
 */
export function returnsRestoreInventory(category: CategoryHierarchy | undefined): boolean {
  const config = getItemTrackingConfig(category);
  return config.trackingType !== 'CONSUMABLE';
}

/**
 * Get the default reorder level for a category.
 * Returns undefined if not set or if tracking type doesn't support it.
 * 
 * @param category - The category to check
 * @returns Default reorder level or undefined
 */
export function getDefaultReorderLevel(category: CategoryHierarchy | undefined): number | undefined {
  const config = getItemTrackingConfig(category);
  if (config.trackingType === 'SERIALIZED') {
    return undefined; // Serialized items don't have reorder levels
  }
  return config.defaultReorderLevel;
}

/**
 * Check if a category requires expiration tracking for items.
 * This is explicitly set via the category configuration checkbox.
 * 
 * @param category - The category to check
 * @returns true if expiration dates are required for items in this category
 */
export function requiresExpiration(category: CategoryHierarchy | undefined): boolean {
  if (!category) {
    return false;
  }
  
  const config = getItemTrackingConfig(category);
  return config.requiresExpiration === true;
}

/**
 * Get the default expiration period in months for a category.
 * Used to auto-calculate expiration dates from manufacture/purchase date.
 * 
 * @param category - The category to check
 * @returns Default expiration period in months, or undefined if not set
 */
export function getDefaultExpirationMonths(category: CategoryHierarchy | undefined): number | undefined {
  if (!category) {
    return undefined;
  }
  
  const config = getItemTrackingConfig(category);
  return config.defaultExpirationMonths;
}

/**
 * Calculate an expiration date from a start date and expiration period.
 * 
 * @param startDate - The manufacture or purchase date (ISO string or Date)
 * @param expirationMonths - Number of months until expiration
 * @returns ISO date string for the expiration date
 */
export function calculateExpirationDate(
  startDate: string | Date,
  expirationMonths: number
): string {
  const date = typeof startDate === 'string' ? new Date(startDate) : startDate;
  date.setMonth(date.getMonth() + expirationMonths);
  return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
}
